import React, { useId, useState } from 'react';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { LIBRARY_FILE_ACCEPT, LIBRARY_FILE_HELP } from '../../Components/KnowledgeLibrary/knowledgeLibraryConfig';

export const PROGRESS_LABELS = {
  queued: 'Queued',
  uploading: 'Uploading',
  uploaded: 'Uploaded',
  indexing: 'Indexing',
  indexed: 'Indexed',
  error: 'Error',
};

const PROGRESS_CHIP = {
  queued: { bg: '#F3F4F6', color: '#374151' },
  uploading: { bg: '#EFF6FF', color: '#1D4ED8' },
  uploaded: { bg: '#EFF6FF', color: '#1D4ED8' },
  indexing: { bg: '#FFFBEB', color: '#B45309' },
  indexed: { bg: '#ECFDF5', color: '#047857' },
  error: { bg: '#FEF2F2', color: '#B91C1C' },
};

/**
 * A drag-and-drop area for library files, with a Choose files button for keyboards, and the list
 * of queued files with their progress. entries: [{ file, state, message }].
 */
export default function KnowledgeDropZone({ entries, onAdd, onRemove, multiple = true, disabled = false, label = 'Drop files here' }) {
  const inputId = useId();
  const [over, setOver] = useState(false);
  const add = (list) => {
    const files = Array.from(list || []);
    if (files.length && !disabled) onAdd(multiple ? files : files.slice(0, 1));
  };
  return (
    <Box>
      <Box
        data-testid="knowledge-drop-zone"
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer?.files); }}
        sx={{
          border: `2px dashed ${over ? '#1D3DFC' : '#CBD5E1'}`,
          borderRadius: '12px',
          bgcolor: over ? '#EEF1FF' : '#FFFFFF',
          p: 3,
          textAlign: 'center',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <CloudUploadOutlinedIcon sx={{ color: '#64748B', fontSize: 32 }} />
        <Typography sx={{ fontWeight: 600, color: '#0F172A', mt: 1 }}>{label}</Typography>
        <Typography sx={{ fontSize: 13, color: '#64748B', mb: 1.5 }}>{LIBRARY_FILE_HELP}</Typography>
        <input
          id={inputId}
          type="file"
          multiple={multiple}
          accept={LIBRARY_FILE_ACCEPT}
          disabled={disabled}
          aria-label="Choose files"
          style={{ display: 'none' }}
          onChange={(e) => { add(e.target.files); e.target.value = ''; }}
        />
        <Button component="label" htmlFor={inputId} variant="outlined" disabled={disabled} sx={{ textTransform: 'none' }}>
          Choose files
        </Button>
      </Box>
      {entries.length ? (
        <Box component="ul" aria-label="Files" sx={{ listStyle: 'none', m: 0, mt: 1.5, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {entries.map((entry, i) => (
            <Box
              component="li"
              key={`${entry.file.name}-${i}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #E5E7EB', borderRadius: '8px', bgcolor: '#fff' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.file.name}</Typography>
                {entry.message ? <Typography sx={{ fontSize: 12, color: entry.state === 'error' ? '#B91C1C' : '#64748B' }}>{entry.message}</Typography> : null}
              </Box>
              <Chip
                size="small"
                label={PROGRESS_LABELS[entry.state] || entry.state}
                aria-label={`${entry.file.name}: ${PROGRESS_LABELS[entry.state] || entry.state}`}
                sx={{ height: 22, fontSize: 12, fontWeight: 500, bgcolor: PROGRESS_CHIP[entry.state]?.bg, color: PROGRESS_CHIP[entry.state]?.color }}
              />
              {entry.state === 'queued' && onRemove ? (
                <Tooltip title="Remove">
                  <IconButton size="small" aria-label={`Remove ${entry.file.name}`} onClick={() => onRemove(i)}><CloseIcon fontSize="small" /></IconButton>
                </Tooltip>
              ) : null}
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
